// ============================================================================
// Requirement Specifications (SRS)
// ============================================================================

export interface ReqSpec {
  id: number
  name: string
  test_project_id: number
  doc_id: string
  title?: string
  scope?: string
  type: string // SRS, BRS, FRS, etc.
  created_at: string
  project_name?: string
  project_prefix?: string
  requirement_count?: number
  requirements?: Requirement[]
}

export interface ReqSpecsResponse {
  req_specs: ReqSpec[]
  limit: number
  offset: number
  count: number
}

export interface CreateReqSpecRequest {
  name: string
  test_project_id: number
  doc_id: string
  title?: string
  scope?: string
  type?: string
}

export interface UpdateReqSpecRequest {
  name?: string
  title?: string
  scope?: string
  type?: string
}

// ============================================================================
// Requirements
// ============================================================================

export interface Requirement {
  id: number
  name: string
  srs_id: number
  req_doc_id: string
  created_at: string
  srs_doc_id?: string
  srs_name?: string
  version_count?: number
  coverage_count?: number
  versions?: ReqVersion[]
  coverage?: ReqCoverage[]
  // Computed fields from latest version (returned by API join)
  status?: RequirementStatus
  type?: RequirementType
  modified_at?: string
  author_name?: string
}

export interface RequirementsResponse {
  requirements: Requirement[]
  limit: number
  offset: number
  count: number
}

export interface CreateRequirementRequest {
  name: string
  srs_id: number
  req_doc_id: string
}

export interface UpdateRequirementRequest {
  name?: string
  req_doc_id?: string
}

// ============================================================================
// Requirement Versions
// ============================================================================

export interface ReqVersion {
  id: number
  requirement_id: number
  version: number
  revision: number
  scope?: string
  status: RequirementStatus
  type: RequirementType
  active: boolean
  author_id: number
  creation_ts: string
  author_name?: string
}

export interface ReqVersionsResponse {
  versions: ReqVersion[]
}

export interface CreateReqVersionRequest {
  requirement_id: number
  version?: number
  revision?: number
  scope?: string
  status?: RequirementStatus
  type?: RequirementType
  author_id: number
}

export interface UpdateReqVersionRequest {
  scope?: string
  status?: RequirementStatus
  type?: RequirementType
  active?: boolean
}

// ============================================================================
// Requirement Coverage (Test Case Links)
// ============================================================================

export interface ReqCoverage {
  id: number
  req_id: number
  req_version_id: number
  testcase_id: number
  tcversion_id: number
  author_id: number
  creation_ts: string
  testcase_name?: string
  importance?: number
  execution_type?: number
  active?: boolean
  author_name?: string
}

export interface ReqCoverageResponse {
  coverage: ReqCoverage[]
}

export interface CreateCoverageRequest {
  req_id: number
  req_version_id: number
  testcase_id: number
  tcversion_id: number
  author_id: number
}

// ============================================================================
// Statistics
// ============================================================================

export interface RequirementStats {
  total_requirements: number
  total_versions: number
  covered_testcases: number
  uncovered_requirements: number
}

// ============================================================================
// Enums
// ============================================================================

export enum RequirementStatus {
  Valid = 'V',
  Draft = 'D',
  Review = 'R',
}

export enum RequirementType {
  Functional = 'F',
  NonFunctional = 'N',
}

// ============================================================================
// Utility Functions
// ============================================================================

export function getRequirementStatusLabel(status: RequirementStatus): string {
  switch (status) {
    case RequirementStatus.Valid:
      return 'Valid'
    case RequirementStatus.Draft:
      return 'Draft'
    case RequirementStatus.Review:
      return 'In Review'
    default:
      return 'Unknown'
  }
}

export function getRequirementStatusColor(
  status: RequirementStatus
): 'default' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case RequirementStatus.Valid:
      return 'success'
    case RequirementStatus.Draft:
      return 'warning'
    case RequirementStatus.Review:
      return 'default'
    default:
      return 'default'
  }
}

export function getRequirementTypeLabel(type: RequirementType): string {
  switch (type) {
    case RequirementType.Functional:
      return 'Functional'
    case RequirementType.NonFunctional:
      return 'Non-Functional'
    default:
      return 'Unknown'
  }
}

// ============================================================================
// Design Requirements Documents (DRDs) - AI Generated
// ============================================================================

export interface QualityMetrics {
  total_functions: number
  total_classes: number
  total_lines_of_code: number
  average_cyclomatic_complexity: number
  test_coverage_estimate: number
  code_duplication_estimate: number
  maintainability_index: number
}

export interface DRDContent {
  quality_metrics?: QualityMetrics
  [key: string]: unknown
}

export interface DRD {
  id: number
  test_project_id: number
  tenant_id: string
  title: string
  version: string
  status: string // draft, approved, deprecated
  content?: DRDContent // JSONB content
  analyzer_used?: string
  confidence_score?: number
  generation_params?: Record<string, unknown>
  markdown_file_path?: string
  pdf_file_path?: string
  created_at: string
  updated_at: string
  created_by?: number
  // Joined fields
  project_name?: string
  project_prefix?: string
  created_by_name?: string
  requirement_count?: number
  requirements?: DRDRequirement[]
}

export interface DRDsResponse {
  drds: DRD[]
  limit: number
  offset: number
  count: number
}

export interface DRDRequirement {
  id: number
  drd_id: number
  requirement_id: string // REQ-001, REQ-002, etc
  requirement_type?: string
  priority?: string
  description: string
  source_file?: string
  created_at: string
}

export interface CreateDRDRequest {
  test_project_id: number
  title: string
  version?: string
  status?: string
  content: Record<string, unknown>
  analyzer_used?: string
  confidence_score?: number
  generation_params?: Record<string, unknown>
  markdown_file_path?: string
  pdf_file_path?: string
}

export interface UpdateDRDRequest {
  title?: string
  version?: string
  status?: string
  content?: Record<string, unknown>
  markdown_file_path?: string
  pdf_file_path?: string
}

export interface CreateDRDRequirementRequest {
  requirement_id: string
  requirement_type?: string
  priority?: string
  description: string
  source_file?: string
}

export function getDRDStatusColor(
  status: string
): 'default' | 'success' | 'warning' | 'destructive' {
  switch (status.toLowerCase()) {
    case 'approved':
      return 'success'
    case 'draft':
      return 'warning'
    case 'deprecated':
      return 'destructive'
    default:
      return 'default'
  }
}
